import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import moment from 'moment';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { trackClickEvent, useIntersectionObserver } from '@/utils/site-track';
import { getArticleMedia } from '@/api/tanzhi';

export interface PostType {
  id: number;
  title: { rendered: string };
  date: string;
  excerpt: { rendered: string };
  link: string;
  featured_media: number;
  categories?: number[];
}

export const Post = (props: {
  data: PostType;
  showImage?: boolean;
  dataObserverId?: string;
}) => {
  const [featuredMediaUrl, setFeaturedMediaUrl] = React.useState<string>('');
  const { data, showImage = false, dataObserverId = 'bottom' } = props;

  const {
    id,
    link,
    title: { rendered: title },
    excerpt: { rendered: excerptContent },
    date,
    featured_media: mediaId,
  } = data;

  React.useEffect(() => {
    if (mediaId) {
      getArticleMedia(data?.featured_media).then(
        (res) => res.status == 200 && setFeaturedMediaUrl(res.data.source_url),
      );
    }
  }, [mediaId]);

  useIntersectionObserver([
    document.querySelector(`[data-observer-id="${dataObserverId}"]`),
  ]);

  if (!data) return null;

  return (
    <Link
      className="block rounded-lg border border-white-smoke bg-white hover:border-2 hover:border-web-blue-500 hover:shadow[0px 5px 10px rgba(0, 0, 0, 0.15)]"
      key={id}
      href={link}
      data-observer-id={`articles_${dataObserverId}_${id}${showImage ? `_${mediaId}` : ''}`}
      data-ele-group={`articles_${dataObserverId}`}
      data-ele-id={`${dataObserverId}_${id}${showImage ? `_${mediaId}` : ''}_${title}`}
      onClick={trackClickEvent}
    >
      {showImage && (
        <div className="relative w-full border-b border-white-smoke rounded-t-lg overflow-hidden pt-[100%]">
          {featuredMediaUrl && (
            <Image
              src={featuredMediaUrl}
              alt="featured image"
              fill
              style={{
                objectFit: 'contain',
              }}
            />
          )}
        </div>
      )}
      <div className="px-5 py-8">
        <p className="font-semibold text-xl mb-4 h-14 opacity-85 line-clamp-2">
          {title}
        </p>
        <div
          className="mb-3 text-sm leading-5.5"
          dangerouslySetInnerHTML={{ __html: excerptContent }}
        />
        <p className="text-sm flex items-center leading-6.5">
          <DateRangeIcon className="mr-3" />
          {moment(date).format('YYYY/MM/DD')}
        </p>
      </div>
    </Link>
  );
};

const RecommendPosts = (props: { posts: PostType[] }) => {
  const { posts } = props;

  if (!posts.length) {
    return null;
  }
  return (
    <div className="w-full px-3 mt-12 grid gap-x-10 gap-y-6 grid-cols-1 tablet:grid-cols-3 tablet:mt-16">
      {posts.map((item, index) => (
        <Post data={item} key={index} showImage={true} />
      ))}
    </div>
  );
};

export default RecommendPosts;
